import React from 'react';
import LegalLayout from '../../components/LegalLayout';
import { COMPANY, LAST_UPDATED } from './companyConfig';

const Terms = () => (
  <LegalLayout
    title="Terms & Conditions"
    subtitle={`Please read these Terms carefully before using ${COMPANY.brand}'s services. By accessing or using our website, you agree to be bound by these Terms.`}
    lastUpdated={LAST_UPDATED}
    canonicalPath="/terms"
    description="Terms and conditions for using ACF Tourism's flight booking platform, including B2C and B2B agent policies, payments, and liability."
  >
    <h2>1. About Us</h2>
    <p>
      <strong>{COMPANY.legalName}</strong> (“{COMPANY.brand}”, “we”, “us”, “our”) is a travel
      services company operating the website <strong>{COMPANY.domain}</strong> and the
      associated booking platform (together, the “Platform”). These Terms and Conditions
      (“Terms”) govern your access to and use of the Platform, whether as a retail
      customer (B2C), a registered travel agent (B2B), or an administrator.
    </p>
    <p>
      Our registered office is at {COMPANY.address}. Our Corporate Identification Number
      (CIN) is {COMPANY.cin}; GSTIN: {COMPANY.gstin}; PAN: {COMPANY.pan}.
    </p>

    <h2>2. Acceptance of Terms</h2>
    <p>
      By accessing, browsing, or making a booking on the Platform, you confirm that you
      have read, understood, and agreed to be bound by these Terms and our
      <a href={COMPANY.urls.privacy}> Privacy Policy</a>. If you do not agree with any part
      of these Terms, please discontinue use of the Platform.
    </p>

    <h2>3. Eligibility</h2>
    <ul>
      <li>You must be at least 18 years of age to make a booking or hold an account.</li>
      <li>You represent that all information you provide is accurate, current, and complete.</li>
      <li>Travel Agents (B2B) must additionally complete admin approval before accessing agent-only features.</li>
    </ul>

    <h2>4. Role of {COMPANY.brand}</h2>
    <p>
      {COMPANY.brand} acts solely as an <strong>intermediary booking agent</strong> between
      you and third-party airlines, hotels, payment processors, and other travel service
      providers (“Service Providers”). We do not operate any flight, hotel, or transport
      service ourselves. The contract of carriage / accommodation is always between you
      and the relevant Service Provider, subject to their own terms, fare rules,
      cancellation policies, and conditions.
    </p>

    <h2>5. Booking Process</h2>
    <ul>
      <li>Fares and availability are fetched in real time from Service Providers and are
          <strong> not confirmed</strong> until a PNR is issued and full payment is successful.</li>
      <li>We reserve the right to cancel or refuse any booking if the Service Provider
          rejects it, if the fare was displayed in error, or if we suspect fraud.</li>
      <li>Prices shown include applicable airline taxes and surcharges as passed through
          by the Service Provider, plus our service fee (taxable under GST).</li>
      <li>You are responsible for verifying traveller names, dates, and documentation
          requirements (passport, visa, infant age, etc.) before confirming a booking.</li>
    </ul>

    <h2>6. Payment</h2>
    <p>
      Payments on the Platform are processed by third-party payment gateways (including
      CCAvenue). {COMPANY.brand} does not store your card, UPI, or banking credentials.
      By submitting a payment instruction, you authorise the gateway to charge the stated
      amount to your chosen method. In case of gateway failure, bookings will not be
      confirmed and any pre-authorised amount will be released by your bank per their
      standard timeline.
    </p>

    <h2>7. Pricing & Taxes</h2>
    <ul>
      <li>All prices are in Indian Rupees (INR) unless stated otherwise.</li>
      <li>
        GST is charged on our service fee under Rule 32(3) of the CGST Rules, 2017
        (Option 1 — GST on markup). Airline base fare and taxes (YQ / YR / K3) are
        collected on behalf of the airline and passed through unchanged.
      </li>
      <li>
        For B2B agents with a valid GSTIN, a GST-compliant tax invoice is issued
        automatically at the time of booking confirmation.
      </li>
    </ul>

    <h2>8. Cancellations, Refunds & Reschedule</h2>
    <p>
      Cancellations, refunds, and reschedule charges are primarily governed by the Service
      Provider's fare rules. Our handling fee structure is described in our
      <a href={COMPANY.urls.cancellation}> Cancellation Policy</a> and
      <a href={COMPANY.urls.refund}> Refund Policy</a>.
    </p>

    <h2>9. Conduct & Prohibited Use</h2>
    <ul>
      <li>Using the Platform for speculative, false, or fraudulent bookings is prohibited.</li>
      <li>
        You may not attempt to scrape, reverse-engineer, rate-limit-abuse, or
        circumvent the security of the Platform.
      </li>
      <li>
        Any activity that violates airline fare rules, tax laws, or RBI FEMA guidelines
        (for international transactions) is strictly prohibited.
      </li>
    </ul>

    <h2>10. Liability</h2>
    <p>
      {COMPANY.brand}'s liability is limited to the service fee we collect. We are not
      liable for losses caused by Service Provider failures, flight delays / cancellations
      / diversions, denied boarding, baggage issues, visa refusals, force-majeure events,
      changes in government regulation, or any indirect, incidental, or consequential
      damages arising out of use of the Platform.
    </p>

    <h2>11. Intellectual Property</h2>
    <p>
      All content on the Platform — logos, text, design, software — is owned by or licensed
      to {COMPANY.legalName} and protected under the Copyright Act, 1957 and applicable
      international treaties. Unauthorised reproduction is prohibited.
    </p>

    <h2>12. Governing Law & Jurisdiction</h2>
    <p>
      These Terms are governed by the laws of India. Any dispute shall be subject to
      the exclusive jurisdiction of the courts in <strong>{COMPANY.jurisdiction}</strong>.
    </p>

    <h2>13. Changes to Terms</h2>
    <p>
      We may update these Terms at any time. Material changes will be notified by a
      prominent notice on the Platform. Continued use after such changes constitutes
      acceptance of the revised Terms.
    </p>

    <h2>14. Contact</h2>
    <p>
      For questions about these Terms, reach us at{' '}
      <a href={`mailto:${COMPANY.legalEmail}`}>{COMPANY.legalEmail}</a> or visit our{' '}
      <a href={COMPANY.urls.contact}>Contact Us</a> page.
    </p>
  </LegalLayout>
);

export default Terms;
